import getItem from "@helpers/items/getItem";
import { getProductPrice } from "@helpers/products/getProductPrice";
import type { Item } from "@typings/product/item";
import type { Product } from "@typings/product/product";

const PARAM_KEY = "share";
const MAX_PARAM_LENGTH = 1500;

type ShareableCartEntry = {
  id: number;
  quantity: number;
  offerId?: number;
  options?: Record<number, number>;
  isFbw?: boolean;
  upsellOrderId?: number;
  variant?: string;
};

const toShareableEntries = (cartItems: Product[]): ShareableCartEntry[] => {
  return cartItems
    .filter((cartItem) => cartItem.item.category?.name !== "Free Gift")
    .map((cartItem) => ({
      id: cartItem.item.id,
      quantity: Math.max(1, cartItem.configuration.quantity),
      offerId: cartItem.configuration.offer?.id,
      options: cartItem.configuration.options,
      isFbw: cartItem.configuration.isFbw,
      upsellOrderId: cartItem.configuration.upsell?.orderId,
      variant: cartItem.configuration.variant,
    }));
};

const sanitizeOptions = (item: Item, options?: Record<number, number>) => {
  return item.options.reduce<Record<number, number>>((acc, option) => {
    const providedValue = options?.[option.id];
    const fallbackValue = option.values[0]?.id;
    const validValue = option.values.find((value) => value.id === providedValue)?.id || fallbackValue;

    if (typeof validValue === "number") {
      acc[option.id] = validValue;
    }

    return acc;
  }, {});
};

export const encodeSharedCartParam = (cartItems: Product[]): string | null => {
  try {
    const entries = toShareableEntries(cartItems);

    if (!entries.length) {
      return null;
    }

    const encoded = encodeURIComponent(JSON.stringify(entries));

    if (encoded.length > MAX_PARAM_LENGTH) {
      return null;
    }

    return encoded;
  } catch (error) {
    console.error("Failed to encode shared cart param", error);
    return null;
  }
};

export const decodeSharedCartParam = (param: string): Product[] | null => {
  try {
    const decoded = decodeURIComponent(param);
    const parsed = JSON.parse(decoded);

    if (!Array.isArray(parsed)) {
      return null;
    }

    const products: Product[] = parsed
      .map((entry: ShareableCartEntry) => {
        if (!entry || typeof entry.id !== "number" || typeof entry.quantity !== "number") {
          return null;
        }

        const item = getItem(entry.id);

        if (!item) {
          return null;
        }

        const offer =
          item.offers.find((o) => o.id === entry.offerId) ||
          item.offers.find((o) => o.name === "Monthly Subscription") ||
          item.offers[0];

        if (!offer) {
          return null;
        }

        const options = sanitizeOptions(item, entry.options);
        const isPromotion = Boolean(entry.isFbw || entry.upsellOrderId);
        const { price, oldPrice } = getProductPrice(item, offer, options, isPromotion, entry.variant);

        return {
          item,
          configuration: {
            price,
            oldPrice,
            offer,
            options,
            quantity: Math.max(1, Math.floor(entry.quantity)),
            isFbw: entry.isFbw,
            upsell: entry.upsellOrderId ? { orderId: entry.upsellOrderId } : undefined,
            variant: entry.variant,
          },
        };
      })
      .filter((product): product is Product => !!product);

    return products.length ? products : null;
  } catch (error) {
    console.error("Failed to decode shared cart param", error);
    return null;
  }
};

export const buildSharedCartUrl = (baseUrl: string, cartItems: Product[]): string => {
  const param = encodeSharedCartParam(cartItems);

  if (!param) {
    return baseUrl;
  }

  const separator = baseUrl.includes("?") ? "&" : "?";

  return `${baseUrl}${separator}${PARAM_KEY}=${param}`;
};

export const sharedCartParamKey = PARAM_KEY;
